import type {
  UniversalSearchResult,
  SearchEntry,
  PromptSuggestionsData,
} from "../types";
import type { GlobalSearchOutput, SearchableAssetType } from "../../schema";

const BFF_BASE = "/api/asset-management-bff/trpc";

async function trpcGet<T>(
  procedure: string,
  input?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const url = input
    ? `${BFF_BASE}/${procedure}?input=${encodeURIComponent(JSON.stringify(input))}`
    : `${BFF_BASE}/${procedure}`;

  const response = await fetch(url, {
    method: "GET",
    headers: { "content-type": "application/json" },
    credentials: "include",
    signal,
  });

  if (!response.ok) {
    throw new Error(`BFF request failed: ${response.status}`);
  }

  const json = await response.json();
  return json.result.data;
}

export async function fetchUniversalSearch(
  query: string,
  resourceTypes: string[],
  limit: number,
  signal?: AbortSignal,
): Promise<UniversalSearchResult> {
  return trpcGet<UniversalSearchResult>(
    "universalSearch.search",
    { query, resourceTypes, limit },
    signal,
  );
}

export async function fetchRecentlyUpdated(
  resourceTypes: string[],
  limit: number,
  signal?: AbortSignal,
): Promise<{ recentZapierAssets: SearchEntry[] }> {
  return trpcGet<{ recentZapierAssets: SearchEntry[] }>(
    "universalSearch.recentlyUpdated",
    { resourceTypes, limit },
    signal,
  );
}

export async function fetchPromptSuggestions(): Promise<PromptSuggestionsData> {
  return trpcGet<PromptSuggestionsData>("promptSuggestions.get");
}

export async function fetchAssetSearch(
  search: string,
  limitPerType: number,
  assetTypes?: readonly SearchableAssetType[],
  signal?: AbortSignal,
): Promise<GlobalSearchOutput> {
  return trpcGet<GlobalSearchOutput>(
    "globalSearch.search",
    {
      search,
      limitPerType,
      // Spread so an omitted opt-in leaves no `assetTypes` key at all: an
      // explicit list would take the server's default out of play.
      ...(assetTypes ? { assetTypes: [...assetTypes] } : {}),
    },
    signal,
  );
}
